/**
 * Print a standalone HTML page without printing the page around it.
 *
 * `window.print()` prints whatever the tool page is — the form, the FAQ, the related
 * links — and the site's print stylesheet can only hide whole regions of it. A template
 * generator's printout should be the document and nothing else, typeset for paper. So
 * the document goes into a hidden same-origin `srcdoc` frame and that frame prints
 * itself: its own `<title>` names the PDF a "Save as PDF" produces, and its own
 * stylesheet sets the fonts and margins.
 *
 * The frame is left in place after printing rather than removed on `afterprint`: Safari
 * returns from `print()` before the dialog closes, and removing the frame then cancels
 * the job. The next print replaces it, so there is never more than one.
 */

const FRAME_ID = "tool-kit-print-frame";

export function printHtmlDocument(page: string): void {
  document.getElementById(FRAME_ID)?.remove();
  const frame = document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.tabIndex = -1;
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  frame.addEventListener(
    "load",
    () => {
      const view = frame.contentWindow;
      try {
        if (!view) {
          throw new Error("The print frame has no window");
        }
        view.focus();
        view.print();
      } catch {
        // A browser that will not print a frame still prints the page.
        window.print();
      }
    },
    { once: true }
  );
  frame.srcdoc = page;
  document.body.appendChild(frame);
}
